/**
 * revenue_pulse — every vertical. By day for the week or by week for the month.
 *
 * TWO READS, CHOSEN BY THE COMPANY'S VERTICALS. Casing alone reads
 * GET /reports/dashboard/revenue-week, as it always has, and draws the same
 * page. A company with any other vertical reads GET /dashboard/revenue-pulse
 * (api#1121), whose casing days are that route's rows exactly, plus every
 * other vertical's:
 *
 *   - ONE CHART PER CURRENCY. The headline is the API's `total` for that
 *     currency. The bars add the verticals' amounts for a day, in that
 *     currency only: the route returns days per vertical, and a bar is the
 *     day across them. Never summed across currencies.
 *   - UNDATED WORK IS BESIDE THE CHART, never on a day: "+ CA$440.00 not dated".
 *   - A VERTICAL THAT IS NOT `ok` IS NAMED under the chart (MoneyVerticalNotes):
 *     unpriced rows, a figure that cannot be stated, or a failed read. None of
 *     them is $0. With nothing countable and a vertical missing, the headline
 *     is —, not $0.00.
 *
 * A FAILED READ IS A FAILURE NOTICE, NOT $0.00. The old Home read this query
 * with `revenueRaw ?? []`, so a 500, a dropped connection or a 403 (the route
 * is FINANCIAL_READ) drew seven empty bars under "$0.00 this week" — the
 * screen of a week with no revenue.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../api/client';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { tenantTodayDate, toCalendarDateString } from '../../../lib/calendar-date';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import RevenuePulse from '../../dashboard/RevenuePulse';
import { useAuth } from '../../../contexts/AuthContext';
import { fmtAmounts } from '../../../lib/currency-amounts';
import type { RevenuePulse as RevenuePulseRead } from '../../../types/overview-money';
import { MoneyVerticalNotes } from '../MoneyVerticalNotes';
import { useRevenuePulse } from '../overviewQueries';
import { hasGlobalMoneyVertical } from '../globalMoney';
import { PeriodToggle, usePeriod, type Period } from '../PeriodToggle';
import { LoadFailureNotice } from '../../ui/LoadFailureNotice';
import { WidgetPanel, WidgetPending } from '../WidgetPanel';

const PERIOD_KEY = 'revenuePeriod';
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Monday of the TENANT's current week, or null for an unknown zone. */
function weekStart(timeZone: string | null): Date | null {
  const d = tenantTodayDate(timeZone);
  if (!d) return null;
  const day = d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  mon.setHours(0, 0, 0, 0);
  return mon;
}

interface Row { day: string | null; revenue: number }

export function revenueChart(rows: Row[], period: Period, timeZone: string | null) {
  if (period === 'month') {
    const weeks = [0, 0, 0, 0, 0];
    for (const r of rows) {
      const d = String(r.day || '').split(/[T ]/)[0];
      const dayNum = parseInt(d.split('-')[2] || '1', 10);
      weeks[Math.min(Math.floor((dayNum - 1) / 7), 4)] += r.revenue || 0;
    }
    const data = weeks.map((rev, i) => ({ day: `Wk ${i + 1}`, revenue: rev }));
    // Drop trailing empty weeks that have not happened yet (as the old Home did).
    const now = tenantTodayDate(timeZone);
    const currentWeek = now ? Math.floor((now.getDate() - 1) / 7) : null;
    while (currentWeek !== null && data.length - 1 > currentWeek && data[data.length - 1].revenue === 0) data.pop();
    return { data, total: data.reduce((s, d) => s + d.revenue, 0) };
  }
  const start = weekStart(timeZone);
  // No zone, no week: an empty chart and a dash rather than seven zero bars.
  if (!start) return { data: [], total: null };
  const data = DAY_LABELS.map((label, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const key = toCalendarDateString(d);
    const match = rows.find((r) => String(r.day || '').split(/[T ]/)[0] === key);
    return { day: label, revenue: match ? match.revenue : 0 };
  });
  return { data, total: data.reduce((s, d) => s + d.revenue, 0) };
}

/**
 * The global read, one chart per currency. Days are summed across verticals
 * within a currency only; the headline is the API's total for that currency.
 */
export function GlobalRevenue({ read, period, timeZone }: { read: RevenuePulseRead; period: Period; timeZone: string | null }) {
  const currencies = Array.from(new Set([
    ...read.total.map((a) => a.currency),
    ...read.verticals.flatMap((v) => v.days.flatMap((d) => d.amounts.map((a) => a.currency))),
  ])).sort();

  const charts = currencies.length === 0
    ? [{ currency: undefined as string | undefined, ...revenueChart([], period, timeZone) }]
    : currencies.map((currency) => {
        const byDay = new Map<string, number>();
        for (const v of read.verticals) for (const d of v.days) for (const a of d.amounts) {
          if (a.currency === currency) byDay.set(d.day, (byDay.get(d.day) ?? 0) + a.amount);
        }
        const chart = revenueChart(Array.from(byDay, ([day, revenue]) => ({ day, revenue })), period, timeZone);
        const apiTotal = read.total.find((a) => a.currency === currency);
        return { currency, data: chart.data, total: apiTotal ? apiTotal.amount : chart.total === null ? null : 0 };
      });

  return (
    <>
      {charts.map((c) => (
        <div key={c.currency ?? 'none'} data-testid="revenue-currency" className="mb-4 last:mb-0">
          <RevenuePulse
            data={c.data}
            // Nothing countable: a real $0.00 only when every vertical was counted.
            total={c.currency === undefined && !read.complete ? null : c.total}
            period={period}
            currency={c.currency}
          />
        </div>
      ))}
      {read.undated_total.length > 0 && (
        <p data-testid="revenue-undated" className="mt-3 text-xs text-fieldiq-muted">
          + {fmtAmounts(read.undated_total, '—')} not dated
        </p>
      )}
      <MoneyVerticalNotes verticals={read.verticals} testId="revenue-vertical-notes" />
    </>
  );
}

export default function RevenuePulseWidget() {
  const { hasVertical } = useAuth();
  const global = hasGlobalMoneyVertical(hasVertical);
  const timeZone = useTenantTimeZone();
  // Revenue reads week unless the viewer chose month.
  const [period, choose] = usePeriod(PERIOD_KEY, 'week');

  const casing = useQuery({
    queryKey: ['home-revenue-week', period],
    queryFn: () => api.reports.revenueWeek(period).then((r) => (r.data.data ?? []) as Row[]),
    enabled: !global,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const pulse = useRevenuePulse(period, global);
  const q = global ? pulse : casing;
  const failure = q.data === undefined ? queryLoadFailure(q) : null;

  const toggle = <PeriodToggle period={period} onChange={choose} />;

  return (
    <WidgetPanel
      widgetKey="revenue_pulse"
      eyebrow="Ticket revenue"
      title="Revenue Pulse"
      aside={toggle}
      link={{ label: 'Reports', to: '/reports' }}
    >
      <div className="p-4">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(q)} kind={failure} subject="revenue" className="py-6" />
        ) : q.isLoading || q.data === undefined ? (
          <WidgetPending rows={3} />
        ) : global ? (
          pulse.data ? (
            <GlobalRevenue read={pulse.data} period={period} timeZone={timeZone} />
          ) : (
            // An envelope with no payload is not a week of $0.
            <LoadFailureNotice error={new Error('No revenue payload')} kind="unreachable" subject="revenue" className="py-6" />
          )
        ) : (
          <RevenuePulse {...revenueChart(casing.data ?? [], period, timeZone)} period={period} />
        )}
      </div>
    </WidgetPanel>
  );
}
